import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SnackbarRenderer } from './SnackbarRenderer.js';
import { SnackbarContext } from './context.js';
import { SnackbarTransitionName } from './types.js';
import { s as styles } from './snackbar-CR_b_R8I.js';
import 'react-transition-group';
import 'classnames';
import '@hh.ru/magritte-common-func-utils';
import '@hh.ru/magritte-common-keyboard';
import '@hh.ru/magritte-common-resize';
import '@hh.ru/magritte-common-use-multiple-refs';
import '@hh.ru/magritte-common-use-swipe';
import '@hh.ru/magritte-ui-action';
import '@hh.ru/magritte-ui-icon/icon';
import '@hh.ru/magritte-ui-layer';
import '@hh.ru/magritte-ui-link';
import './useAriaSnackbar.js';
import '@hh.ru/magritte-ui-timer';

const SnackbarContextProvider = ({ children }) => {
    const animationTimeoutRef = useRef({
        [SnackbarTransitionName.Show]: 0,
        [SnackbarTransitionName.Hide]: 0,
        [SnackbarTransitionName.Autohide]: 0,
        [SnackbarTransitionName.Replace]: 0,
        [SnackbarTransitionName.RemoveImmediately]: 0,
    });
    const containerRefs = useRef({});
    const isShownRef = useRef({});
    const [state, setState] = useState({});
    const animateOut = useCallback((uid, transition) => {
        setState((prevState) => {
            if (uid in prevState) {
                const prevTransition = prevState[uid].transition;
                const isCurrentlyVisible = prevTransition === SnackbarTransitionName.Show ||
                    prevTransition === SnackbarTransitionName.Replace;
                const willBeHidden = transition === SnackbarTransitionName.Hide || transition === SnackbarTransitionName.Autohide;
                if (transition === SnackbarTransitionName.RemoveImmediately ||
                    (willBeHidden && !isCurrentlyVisible)) {
                    return Object.fromEntries(Object.entries(prevState).filter(([key]) => key !== uid));
                }
                return { ...prevState, [uid]: { ...prevState[uid], transition } };
            }
            return prevState;
        });
    }, [setState]);
    const animateIn = useCallback((uid, props) => {
        setState((prevState) => {
            if (uid in prevState) {
                // не даем менять некоторые пропсы у существующего снекбара
                const { autohideTime, containerId, showTimer, ..._props } = props;
                return { ...prevState, [uid]: { ...prevState[uid], ..._props } };
            }
            const _props = {
                animateOut: (transition) => animateOut(uid, transition),
                isShown: () => !!isShownRef.current[uid],
                onShown: () => (isShownRef.current[uid] = true),
                transition: SnackbarTransitionName[Object.keys(prevState).length > 0 ? 'Replace' : 'Show'],
            };
            return Object.keys(prevState).reduce((accumulator, key) => {
                const value = { ...prevState[key], transition: SnackbarTransitionName.Hide };
                return key === uid ? accumulator : { ...accumulator, [key]: value };
            }, { [uid]: { ..._props, ...props } });
        });
    }, [animateOut, setState]);
    const renderFunc = useCallback((uid) => {
        const containerId = state[uid].containerId;
        const container = containerId ? containerRefs.current[containerId] : document.body;
        if (!container) {
            return null;
        }
        return createPortal(jsx(SnackbarRenderer, { ...state[uid], timeout: animationTimeoutRef.current[state[uid].transition] }), container, uid);
    }, [state]);
    const setContainerRef = useCallback((containerId, instance) => {
        if (!instance) {
            return;
        }
        containerRefs.current[containerId] = instance;
    }, []);
    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles['snackbar-animation-timeout']);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        for (const transition of Object.values(SnackbarTransitionName)) {
            const animationTimeout = parseInt(style.getPropertyValue(`--${transition}-animation-duration`), 10);
            if (Number.isInteger(animationTimeout)) {
                animationTimeoutRef.current[transition] = animationTimeout;
            }
        }
        document.body.removeChild(animationTimeoutElement);
    }, []);
    const value = useMemo(() => {
        return {
            animateIn,
            animateOut,
            isShown: (uid) => isShownRef.current[uid],
            setContainerRef,
        };
    }, [animateIn, animateOut, setContainerRef]);
    return (jsxs(SnackbarContext.Provider, { value: value, children: [Object.keys(state).map(renderFunc), children] }));
};

export { SnackbarContextProvider };
//# sourceMappingURL=SnackbarContextProvider.js.map
